import React, { useState } from 'react';

// Mock data structures - will be replaced with API responses
// Analysis represents a code analysis performed from a Jira issue
const MOCK_ANALYSES = [
  {
    id: 'a1',
    issueKey: 'PROJ-123',
    issueSummary: 'Update authentication flow',
    repository: 'acme/auth-service',
    timestamp: '2024-01-25T15:30:00Z',
    files: ['src/auth/login.js', 'src/auth/session.js'],
    status: 'completed'  // Possible values: completed, in_progress, failed
  },
  // ... more analyses
];

// SystemChange represents an AI-analyzed commit with impact assessment
const MOCK_CHANGES = [
  {
    id: 'c1',
    commitId: 'abc123',
    repository: 'acme/auth-service',
    timestamp: '2024-01-25T14:00:00Z',
    summary: 'Refactored authentication flow to use JWT tokens instead of session cookies. Added token refresh mechanism.',
    impact: 'Major change to authentication system',  // AI-generated impact assessment
    files: ['auth/*', 'middleware/*']  // Files or patterns affected
  },
  // ... more changes
];

/**
 * Dashboard Component
 * 
 * Provides a centralized view of:
 * 1. Analysis History - Code analyses performed from Jira issues
 * 2. System Changes - AI-analyzed commit history showing system evolution
 * 
 * Features:
 * - Tabbed navigation between views
 * - Share links for analyses
 * - Links to Jira issues
 * - Temporal view of system changes
 */
const Dashboard = () => {
  // Track which tab is currently active
  const [activeTab, setActiveTab] = useState('analyses');

  // Helper to format timestamps consistently
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div style={styles.container}>
      {/* Tab Navigation */}
      <div style={styles.tabs}>
        <button 
          style={{
            ...styles.tab,
            ...(activeTab === 'analyses' ? styles.activeTab : {})
          }}
          onClick={() => setActiveTab('analyses')}
        >
          Analysis History
        </button>
        <button 
          style={{
            ...styles.tab,
            ...(activeTab === 'changes' ? styles.activeTab : {})
          }}
          onClick={() => setActiveTab('changes')}
        >
          System Changes
        </button>
      </div>

      {/* Analysis History Tab */}
      {activeTab === 'analyses' ? (
        <div style={styles.content}>
          <h2 style={styles.heading}>Recent Analyses</h2>
          {MOCK_ANALYSES.map(analysis => (
            <div key={analysis.id} style={styles.card}>
              <div style={styles.cardHeader}>
                <h3 style={styles.cardTitle}>
                  {/* Link to Jira issue */}
                  <a href={`/browse/${analysis.issueKey}`} style={styles.link}>
                    {analysis.issueKey}
                  </a>
                  {' - '}
                  {analysis.issueSummary}
                </h3>
                {/* Share button - will open sharing dialog */}
                <button style={styles.shareButton}>Share</button>
              </div>
              <div style={styles.cardMeta}>
                <span>Repository: {analysis.repository}</span>
                <span>•</span>
                <span>{formatDate(analysis.timestamp)}</span>
              </div>
              <div style={styles.filesList}>
                Files analyzed: {analysis.files.join(', ')}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* System Changes Tab */
        <div style={styles.content}>
          <h2 style={styles.heading}>System Changes</h2>
          {MOCK_CHANGES.map(change => (
            <div key={change.id} style={styles.card}>
              <div style={styles.cardHeader}>
                <h3 style={styles.cardTitle}>
                  {change.repository}
                  {' - '}
                  {/* Display shortened commit hash */}
                  <span style={styles.commitId}>{change.commitId.substring(0, 7)}</span>
                </h3>
                <span style={styles.timestamp}>{formatDate(change.timestamp)}</span>
              </div>
              {/* AI-generated summary of changes */}
              <div style={styles.summary}>{change.summary}</div>
              {/* Impact assessment */}
              <div style={styles.impact}>
                Impact: <span style={styles.impactText}>{change.impact}</span>
              </div>
              <div style={styles.filesList}>
                Changed files: {change.files.join(', ')}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Styles following Atlassian Design System patterns
const styles = {
  // Layout styles
  container: {
    padding: '20px',
    maxWidth: '1200px',
    margin: '0 auto'
  },
  
  // Tab navigation styles
  tabs: {
    marginBottom: '20px',
    borderBottom: '2px solid #DFE1E6'
  },
  tab: {
    padding: '10px 20px',
    fontSize: '14px',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    marginRight: '10px'
  },
  activeTab: {
    borderBottom: '2px solid #0052CC',
    color: '#0052CC',
    fontWeight: 'bold'
  },
  
  // Content area styles
  content: {
    padding: '20px 0'
  },
  heading: {
    fontSize: '24px',
    marginBottom: '20px',
    color: '#172B4D'
  },
  
  // Card styles
  card: {
    padding: '16px',
    marginBottom: '16px',
    border: '1px solid #DFE1E6',
    borderRadius: '3px',
    backgroundColor: '#FFFFFF'
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px'
  },
  cardTitle: {
    fontSize: '16px',
    margin: 0,
    color: '#172B4D'
  },
  
  // Metadata styles
  cardMeta: {
    fontSize: '12px',
    color: '#6B778C',
    display: 'flex',
    gap: '8px',
    marginBottom: '8px'
  },
  
  // Interactive element styles
  link: {
    color: '#0052CC',
    textDecoration: 'none'
  },
  shareButton: {
    padding: '4px 8px',
    fontSize: '12px',
    border: '1px solid #0052CC',
    borderRadius: '3px',
    backgroundColor: '#FFFFFF',
    color: '#0052CC',
    cursor: 'pointer'
  },
  
  // Content detail styles
  filesList: {
    fontSize: '12px',
    color: '#6B778C',
    marginTop: '8px'
  },
  commitId: {
    fontFamily: 'monospace',
    color: '#6B778C'
  },
  timestamp: {
    fontSize: '12px',
    color: '#6B778C'
  },
  summary: {
    fontSize: '14px',
    color: '#172B4D',
    marginBottom: '8px',
    lineHeight: '1.4'
  },
  impact: {
    fontSize: '12px',
    color: '#6B778C'
  },
  impactText: {
    color: '#172B4D',
    fontWeight: '500'
  }
};

export default Dashboard; 